import { useGameState } from '../hooks/useGameState';
import { calculateTodayGrowthPoints, maxStageForRarity } from '../domain/economy';
import { getStoneById, stones as allStones } from '../data/stones';
import { StoneCarousel } from '../components/StoneCarousel';
import type { Screen } from '../App';

interface HomeScreenProps {
  onNavigate: (next: Screen, stoneId?: string) => void;
}

export function HomeScreen({ onNavigate }: HomeScreenProps) {
  const { state, ownedStone, setState } = useGameState();
  const todayPts = calculateTodayGrowthPoints(state.daily.consecutiveDays);

  // 所持している石のみをカルーセル表示
  const ownedStones = state.inventory
    .map((o) => getStoneById(o.stoneId))
    .filter((s): s is NonNullable<typeof s> => s != null);

  // 選択中インデックス(selectedStoneId → index)
  const selectedIndex = (() => {
    if (state.selectedStoneId) {
      const idx = ownedStones.findIndex((s) => s.id === state.selectedStoneId);
      if (idx >= 0) return idx;
    }
    return 0;
  })();

  const stages = ownedStones.map(
    (s) => ownedStone(s.id)?.currentStage ?? 0,
  );

  const handleSelect = (index: number) => {
    const stone = ownedStones[index];
    if (!stone) return;
    setState((s) => ({ ...s, selectedStoneId: stone.id }));
  };

  // 育成対象(中央の石)
  const focusStone = ownedStones[selectedIndex];
  const focusOwned = focusStone ? ownedStone(focusStone.id) : undefined;
  const focusStage = focusOwned?.currentStage ?? 0;
  const focusMax = focusStone ? maxStageForRarity(focusStone.rarity) : 0;

  const totalOwned = state.inventory.length;
  const totalAll = allStones.length;

  return (
    <div className="screen home">
      <h1 className="home__greeting">今日も、石と一つになる</h1>
      <p className="home__sub">
        連続 <strong>{state.daily.consecutiveDays}</strong> 日目 · 本日{' '}
        <strong>+{todayPts}</strong> pt
      </p>

      {/* 中央石カルーセル */}
      <StoneCarousel
        stones={ownedStones}
        stages={stages}
        selectedIndex={Math.max(0, selectedIndex)}
        onSelect={handleSelect}
        renderEmpty={() => (
          <div className="empty-state">
            <div className="empty-state__icon">✦</div>
            <div className="empty-state__msg">まだ石がありません</div>
            <button
              className="btn btn--primary"
              onClick={() => onNavigate('gacha')}
              type="button"
            >
              ガチャを引いて迎えよう
            </button>
          </div>
        )}
      />

      {/* 育成ボタン(中央の石に対して) */}
      {focusStone && focusOwned && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            className="btn btn--primary btn--big"
            onClick={() => onNavigate('detail', focusStone.id)}
            type="button"
          >
            {state.daily.tapUsed
              ? '今日の育成は完了'
              : `${focusStone.name}の光を育てる +${todayPts}pt`}
          </button>
          <p
            className="muted"
            style={{ textAlign: 'center', margin: 0, fontSize: '0.8rem' }}
          >
            段階 {focusStage} / {focusMax} · 通算 {focusOwned.growthPoints} pt
          </p>
        </div>
      )}

      {/* 3アクション(派手カード) */}
      <div className="home__actions">
        <ActionCard
          icon="✦"
          title="今日のガチャ"
          desc={
            state.daily.gachaUsed
              ? '本日の分は使用済み'
              : '1日1回無料 / レア10%'
          }
          disabled={state.daily.gachaUsed}
          cta={state.daily.gachaUsed ? '済み' : '引く'}
          onClick={() => onNavigate('gacha')}
        />
        <ActionCard
          icon="❀"
          title="ショップ補充"
          desc={
            state.daily.shopRefillUsed
              ? '本日の補充は済み'
              : '低ティア石を交換'
          }
          disabled={state.daily.shopRefillUsed}
          cta={state.daily.shopRefillUsed ? '済み' : '補充'}
          onClick={() => onNavigate('shop')}
        />
        <ActionCard
          icon="✿"
          title="コレクション"
          desc={`${totalOwned} / ${totalAll} 種 所持`}
          cta="開く"
          onClick={() => onNavigate('collection')}
        />
      </div>
    </div>
  );
}

function ActionCard({
  icon,
  title,
  desc,
  cta,
  onClick,
  disabled,
}: {
  icon: string;
  title: string;
  desc: string;
  cta: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <div className={`action-card ${disabled ? 'is-disabled' : ''}`}>
      <div className="action-card__icon">{icon}</div>
      <div className="action-card__body">
        <div className="action-card__title">{title}</div>
        <div className="action-card__desc">{desc}</div>
      </div>
      <button
        className="btn btn--primary action-card__btn"
        onClick={onClick}
        disabled={disabled}
        type="button"
      >
        {cta}
      </button>
    </div>
  );
}
